import React, { useState } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB } from '../../shared/styles';

function AppointmentInviteScreen({ lang, invite, userName, onAccept, onDecline, modal }) {
  const nl = lang === 'nl';
  const [responderName, setResponderName] = useState(userName || '');
  const [responded, setResponded] = useState(null); // null | 'accept' | 'decline'
  const [loading, setLoading] = useState(false);

  const needsName = !userName;

  const handleRespond = async (action) => {
    if (needsName && !responderName.trim()) return;
    setLoading(true);
    setResponded(action);
    setTimeout(() => {
      setLoading(false);
      if (action === 'accept') onAccept(invite);
      else onDecline(invite);
    }, 1400);
  };

  const dateLabel = invite.appointment_date
    ? new Date(invite.appointment_date + 'T00:00:00').toLocaleDateString(nl ? 'nl-NL' : 'en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })
    : (invite.day || '');

  const content = (
    <div style={{
      background: _T.bg3,
      border: `1px solid ${_T.accent}44`,
      borderRadius: 24,
      padding: '28px 22px 24px',
      maxWidth: 420,
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Accent glow */}
      <div style={{ position:'absolute',top:-40,right:-40,width:160,height:160,borderRadius:'50%',background:`radial-gradient(circle,${_T.accent}18 0%,transparent 70%)`,pointerEvents:'none' }}/>

      {responded ? (
        /* Response confirmation */
        <div style={{ textAlign:'center',padding:'20px 0' }}>
          <div style={{ fontSize:52,marginBottom:14 }}>{responded==='accept'?'✅':'❌'}</div>
          <div style={{ color:C.white,fontSize:18,fontWeight:700,marginBottom:8 }}>
            {responded==='accept'
              ? (nl?'Geaccepteerd!':'Accepted!')
              : (nl?'Geweigerd':'Declined')}
          </div>
          <div style={{ color:C.silver,fontSize:13,lineHeight:1.5 }}>
            {responded==='accept'
              ? (nl?'De afspraak is toegevoegd aan je agenda.':'The appointment has been added to your agenda.')
              : (nl?'Je hebt de uitnodiging geweigerd.':'You\'ve declined the invitation.')}
          </div>
          <div style={{ fontSize:11,color:C.silver,marginTop:12,opacity:0.6 }}>{nl?'Een moment...':'One moment...'}</div>
        </div>
      ) : (
        <>
          {/* Header */}
          <div style={{ fontSize:10,color:_T.accent,letterSpacing:3,fontWeight:700,marginBottom:16 }}>
            ✦ {nl?'UITNODIGING':'INVITATION'}
          </div>

          {/* Appointment title */}
          <div style={{ color:C.white,fontSize:22,fontWeight:800,marginBottom:6,lineHeight:1.2 }}>
            {invite.title}
          </div>

          {/* Creator */}
          {invite.creator_name && (
            <div style={{ color:C.silver,fontSize:13,marginBottom:16 }}>
              {nl?'Van':'From'}: <span style={{ color:C.silver2,fontWeight:600 }}>{invite.creator_name}</span>
            </div>
          )}

          {/* Date & time */}
          <div style={{ background:_T.bg4,borderRadius:14,padding:'12px 14px',marginBottom:16,display:'flex',flexDirection:'column',gap:6 }}>
            {dateLabel && (
              <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                <span style={{ fontSize:16 }}>📅</span>
                <span style={{ color:C.white,fontSize:13,fontWeight:600 }}>{dateLabel}</span>
              </div>
            )}
            {(invite.start || invite.start_time) && (
              <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                <span style={{ fontSize:16 }}>⏰</span>
                <span style={{ color:C.silver2,fontSize:13 }}>
                  {invite.start || invite.start_time}
                  {(invite.end || invite.end_time) ? ` – ${invite.end || invite.end_time}` : ''}
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          {invite.description && (
            <div style={{ color:C.silver,fontSize:13,lineHeight:1.6,marginBottom:16,fontStyle:'italic' }}>
              "{invite.description}"
            </div>
          )}

          {/* Name input for anonymous users */}
          {needsName && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:11,color:C.silver,fontWeight:600,marginBottom:6 }}>
                {nl?'Jouw naam':'Your name'}
              </div>
              <input
                value={responderName}
                onChange={e => setResponderName(e.target.value)}
                placeholder={nl?'Vul je naam in...':'Enter your name...'}
                style={{ width:'100%',background:_T.bg4,border:`1px solid ${_T.cardBorderBot}`,color:C.white,borderRadius:10,padding:'10px 12px',fontSize:14,boxSizing:'border-box' }}
              />
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display:'flex',gap:10 }}>
            <button
              onClick={() => handleRespond('accept')}
              disabled={loading || (needsName && !responderName.trim())}
              style={{
                flex:1,
                background:`linear-gradient(135deg,#4AC87A,#2A9E82)`,
                border:'none',borderRadius:14,padding:'14px 10px',
                color:'#0a1220',fontSize:15,fontWeight:800,cursor:'pointer',
                opacity:(needsName && !responderName.trim())||loading?0.5:1,
                transition:'opacity 0.2s',
              }}>
              ✓ {nl?'Accepteren':'Accept'}
            </button>
            <button
              onClick={() => handleRespond('decline')}
              disabled={loading || (needsName && !responderName.trim())}
              style={{
                flex:1,
                background:'transparent',
                border:`1.5px solid ${C.red}`,borderRadius:14,padding:'14px 10px',
                color:C.red,fontSize:15,fontWeight:700,cursor:'pointer',
                opacity:(needsName && !responderName.trim())||loading?0.5:1,
                transition:'opacity 0.2s',
              }}>
              ✕ {nl?'Weigeren':'Decline'}
            </button>
          </div>

          {/* Skip link */}
          <div style={{ textAlign:'center',marginTop:14 }}>
            <button onClick={() => onDecline({ ...invite, skip: true })}
              style={{ background:'none',border:'none',color:`${C.silver}88`,fontSize:11,cursor:'pointer',textDecoration:'underline' }}>
              {nl?'Later beslissen':'Decide later'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  if (modal) {
    return (
      <div style={{ position:'fixed',inset:0,zIndex:9000,background:'rgba(0,0,0,0.85)',display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
        {content}
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh',background:_T.bg2,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:20 }}>
      {/* Brand mark */}
      <div style={{ fontSize:11,color:_T.accent,letterSpacing:3,marginBottom:24,fontWeight:700 }}>SENSE LIFTING</div>
      {content}
    </div>
  );
}

export default AppointmentInviteScreen;
